(() => {
  if (window.StockamollanBackInStock) {
    window.StockamollanBackInStock.init();
    return;
  }

  const API_REVISION = '2026-07-15';
  const modalControllers = new WeakMap();
  let pubSubBound = false;

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const setButtonState = (trigger, variant) => {
    const buttonGroup = trigger.closest('.product-form__buttons');
    const addButton = buttonGroup?.querySelector('[data-stockamollan-add-button]');
    const paymentButton = buttonGroup?.querySelector('.shopify-payment-button');

    if (!variant) {
      trigger.hidden = true;
      if (addButton) addButton.hidden = false;
      if (paymentButton) paymentButton.hidden = false;
      delete trigger.dataset.variantId;
      return;
    }

    trigger.dataset.variantId = String(variant.id);
    const soldOut = variant.available === false;
    trigger.hidden = !soldOut;
    if (addButton) addButton.hidden = soldOut;
    if (paymentButton) paymentButton.hidden = soldOut;
  };

  const updateSection = (sectionId, variant) => {
    document
      .querySelectorAll(
        '[data-stockamollan-bis-trigger][data-section-id="' + escapeSelector(sectionId) + '"]'
      )
      .forEach((trigger) => setButtonState(trigger, variant));
  };

  class ModalController {
    constructor(modal) {
      this.modal = modal;
      this.messages = JSON.parse(modal.querySelector('[data-stockamollan-bis-messages]')?.textContent || '{}');
      this.form = modal.querySelector('[data-stockamollan-bis-form]');
      this.error = modal.querySelector('[data-stockamollan-bis-error]');
      this.success = modal.querySelector('[data-stockamollan-bis-success]');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitText = modal.querySelector('[data-stockamollan-bis-submit-text]');
      this.spinner = modal.querySelector('.stockamollan-bis__spinner');
      this.companyId = modal.dataset.companyId.trim();
      this.listId = modal.dataset.listId.trim();
      this.variantId = '';
      this.lastTrigger = null;
      this.submitting = false;
      this.successfulBisKey = '';
      this.boundSubmit = this.handleSubmit.bind(this);
      this.boundClick = this.handleModalClick.bind(this);
      this.boundCancel = this.handleCancel.bind(this);
      this.boundKeydown = this.trapFocus.bind(this);

      this.form.addEventListener('submit', this.boundSubmit);
      this.form.addEventListener('input', (event) => this.clearFieldError(event.target));
      this.modal.addEventListener('click', this.boundClick);
      this.modal.addEventListener('cancel', this.boundCancel);
      this.modal.addEventListener('keydown', this.boundKeydown);
      this.modal.addEventListener('close', () => {
        document.body.classList.remove('stockamollan-bis-open');
        if (this.lastTrigger?.isConnected) this.lastTrigger.focus();
      });
    }

    open(trigger) {
      this.lastTrigger = trigger;
      this.variantId = trigger.dataset.variantId || '';
      this.clearGeneralError();
      if (!this.success.hidden) {
        this.success.hidden = true;
        this.form.hidden = false;
      }
      if (!this.modal.open) this.modal.showModal();
      document.body.classList.add('stockamollan-bis-open');
      window.requestAnimationFrame(() => this.form.elements.first_name?.focus());
    }

    close() {
      if (this.submitting) return;
      this.modal.close();
    }

    handleModalClick(event) {
      if (event.target.matches('[data-stockamollan-bis-close]') || event.target.closest('[data-stockamollan-bis-close]')) {
        this.close();
        return;
      }
      if (event.target === this.modal) this.close();
    }

    handleCancel(event) {
      event.preventDefault();
      this.close();
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.modal.querySelectorAll(
          'button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), textarea:not([disabled]):not([hidden]), [href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    clearFieldError(input) {
      if (!input?.name) return;
      input.removeAttribute('aria-invalid');
      const message = this.form.querySelector('[data-error-for="' + escapeSelector(input.name) + '"]');
      if (message) {
        message.hidden = true;
        message.textContent = '';
      }
      this.clearGeneralError();
    }

    setFieldError(input, message) {
      input.setAttribute('aria-invalid', 'true');
      const error = this.form.querySelector('[data-error-for="' + escapeSelector(input.name) + '"]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    validate() {
      let firstInvalid = null;
      const required = [
        ['first_name', this.messages.requiredFirstName],
        ['last_name', this.messages.requiredLastName],
        ['email', this.messages.requiredEmail],
      ];

      required.forEach(([name, message]) => {
        const input = this.form.elements[name];
        this.clearFieldError(input);
        if (!input.value.trim()) {
          this.setFieldError(input, message);
          firstInvalid ||= input;
        }
      });

      const email = this.form.elements.email;
      if (email.value.trim() && !email.validity.valid) {
        this.setFieldError(email, this.messages.invalidEmail);
        firstInvalid ||= email;
      }

      if (firstInvalid) {
        firstInvalid.focus();
        return false;
      }
      return true;
    }

    clearGeneralError() {
      this.error.hidden = true;
      this.error.textContent = '';
    }

    showGeneralError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus?.();
    }

    setLoading(loading) {
      this.submitting = loading;
      this.form.setAttribute('aria-busy', String(loading));
      this.submitButton.disabled = loading;
      this.submitText.textContent = loading ? this.messages.sending : this.messages.submit;
      this.spinner.hidden = !loading;
    }

    async post(endpoint, payload) {
      const response = await fetch(
        'https://a.klaviyo.com' + endpoint + '?company_id=' + encodeURIComponent(this.companyId),
        {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: API_REVISION,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error('request_failed');
    }

    backInStockPayload(profile) {
      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: profile,
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + this.variantId,
              },
            },
          },
        },
      };
    }

    newsletterPayload(profile) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Stockamöllan lagerpåminnelse',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  ...profile,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED',
                      },
                    },
                  },
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.listId,
              },
            },
          },
        },
      };
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.submitting || !this.validate()) return;

      const consent = this.form.elements.marketing_consent.checked;
      if (!this.companyId) {
        this.showGeneralError(this.messages.unconfigured);
        return;
      }
      if (!this.variantId) {
        this.showGeneralError(this.messages.chooseVariant);
        return;
      }
      if (consent && !this.listId) {
        this.showGeneralError(
          this.messages.newsletterUnconfigured
        );
        return;
      }

      const profile = {
        email: this.form.elements.email.value.trim(),
        first_name: this.form.elements.first_name.value.trim(),
        last_name: this.form.elements.last_name.value.trim(),
      };
      const bisKey = profile.email.toLowerCase() + '|' + this.variantId;

      this.clearGeneralError();
      this.setLoading(true);

      try {
        if (this.successfulBisKey !== bisKey) {
          await this.post('/client/back-in-stock-subscriptions', this.backInStockPayload(profile));
          this.successfulBisKey = bisKey;
        }
        if (consent) {
          await this.post('/client/subscriptions', this.newsletterPayload(profile));
        }

        this.form.reset();
        this.form.hidden = true;
        this.success.hidden = false;
        this.success.focus();
      } catch (_error) {
        this.showGeneralError(
          this.messages.requestFailed
        );
      } finally {
        this.setLoading(false);
      }
    }
  }

  const getController = (modal) => {
    if (!modalControllers.has(modal)) modalControllers.set(modal, new ModalController(modal));
    return modalControllers.get(modal);
  };

  const bindPubSub = () => {
    if (
      pubSubBound ||
      !window.FoxTheme?.pubsub?.subscribe ||
      !window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange
    ) return;
    pubSubBound = true;
    window.FoxTheme.pubsub.subscribe(window.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
      updateSection(event.data.sectionId, event.data.variant);
    });
  };

  const init = () => {
    document.querySelectorAll('[data-stockamollan-bis-modal]').forEach(getController);
    bindPubSub();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-stockamollan-bis-trigger]');
    if (!trigger) return;
    event.preventDefault();
    const modal = document.getElementById(trigger.dataset.modalId);
    if (modal) getController(modal).open(trigger);
  });

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('DOMContentLoaded', init);
  window.StockamollanBackInStock = { init };
  init();
})();
